<template>
  <div ref="rootRef" class="dropdown" :class="{ show: isShow }">
    <ButtonElement
        :class="[stateDependantClass, buttonClass]"
        class="btn--ms btn--select btn--regular"
        id="dropdownMenuButton"
        :aria-expanded="isShow"
        @click="toggleDropdown"
        :is-disable="isDisable"
    >
      <div v-if="type === 'button'" class="button-state">
        <span
            class="icon"
            v-if="selectedItem?.icon || selectedItem?.svg"
            style="margin-right:10px;"
        >
          <LucideIcon v-if="icon" :icon="selectedItem?.icon" />
          <img v-if="svg" :src="assetUrl(`assets/${svg}`)" />
        </span>
        <span>{{ name }}</span>
      </div>
      <div
          v-if="type === 'buttonselect' || type === 'buttonselectlarge'"
          class="selectbox-state"
      >
        <div class="selectbox-state-content">
          <span
              class="icon"
              v-if="selectedItem?.icon || selectedItem?.svg"
              style="margin-right:10px;"
          >
            <LucideIcon
                v-if="selectedItem?.icon"
                :icon="selectedItem?.icon"
            />
            <img
                v-if="selectedItem?.svg"
                :src="assetUrl(`assets/${selectedItem?.svg}`)"
            />
          </span>
          <span>{{ staticLabelMode ? name : selectedItem?.name }}</span>
        </div>
        <ChevronDown style="font-size: 16px" aria-hidden="true" />
      </div>
    </ButtonElement>

    <ul
        class="dropdown-menu"
        :class="[{ show: isShow }, dropdowncss]"
        id="dropdownMenu"
    >
      <div
          ref="dropdownContainer"
          class="dropdown-menu-box animation scrollbar"
          style="max-height:260px; overflow-y: auto"
      >
        <div class="dropdown-menu-search">
          <div class="search-wrapper" :class="{ focused: isInputFocused }">
            <div class="icon">
              <Search aria-hidden="true" />
            </div>
            <input
                type="search"
                placeholder="Search"
                v-model="searchQuery"
                ref="searchInput"
                @focus="handleFocus"
                @blur="handleBlur"
                @keydown="handleKeyDown"
            />
          </div>
        </div>
        <div class="dropdown-menu-list">
          <div class="dropdown-menu-head">{{ title }}</div>
          <li v-for="(option, index) in filteredOptions" :key="index">

            <div v-if="option.headline" class="dropdown-menu-head">
              {{ option.headline }}
            </div>

            <a
                v-if="option.name"
                :id="option.name"
                @click="!option.disabled && setSelectedItem(index)"
                @mouseenter="!option.disabled && hoverOnMenuItem(index)"
                :class="['dropdown-item', { active: index === activeMenuItemIndex, disabled: option.disabled }]"
                ref="optionItems"
            >
              <div class="icon" v-if="option.icon || option.svg">
                <LucideIcon v-if="option.icon" :icon="option.icon" />
                <img v-if="option.svg" :src="assetUrl(`assets/${option.svg}`)" />
              </div>
              <div class="content">
                <h6>{{ option.name }}</h6>
                <p>{{ option.content }}</p>
              </div>
              <div class="summary" v-if="option.summary">
                {{ option.summary }}
              </div>
              <div class="arrow" v-if="option.subOptions">
                <ChevronRight aria-hidden="true" />
              </div>
              <div class="key">
                <span>
                  <CornerDownLeft aria-hidden="true" />
                </span>
              </div>
            </a>

            <hr class="dropdown-divider" v-if="option.hasDivider" />
          </li>
        </div>
      </div>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ChevronDown, ChevronRight, CornerDownLeft, Search } from '@lucide/vue'
import {computed, getCurrentInstance, nextTick, ref, useTemplateRef, watch} from 'vue';
import type {DropdownNs} from '@shared/types';
import {useDropdown} from '@shared/composables/use-dropdown';
import ButtonElement from '@shared/components/elements/button/button.element.vue';

const $image = (getCurrentInstance()?.proxy as unknown as {$image?: (url: string) => string})?.$image;
const assetUrl = (url: string): string => $image?.(url) ?? url;

const props = withDefaults(defineProps<{
  value?: string;
  title?: string;
  options: DropdownNs.DropdownOptionsItem[];
  staticLabelMode?: boolean;
  type?: 'button' | 'buttonselect' | 'buttonselectlarge';
  placeholder?: string;
  name?: string;
  buttonClass?: string;
  icon?: string;
  iconType?: string;
  svg?: string;
  isDisable?: boolean;
  dropdowncss?: string;
}>(), {
  staticLabelMode: false,
  type: 'button',
  name: '',
  buttonClass: '',
});

const emit = defineEmits<{
  (e: 'change', item: DropdownNs.DropdownOptionsItem): void;
  (e: 'update:value', value: string | undefined): void;
}>();

const {isOpen, rootRef, toggle, close} = useDropdown();
// Template alias — preserves the existing CSS hook `:class="{ show: isShow }"`.
const isShow = isOpen;

const selectedItem  = ref<DropdownNs.DropdownOptionsItem | undefined>(
  props.options.find(item => item.value === props.value),
);
const activeMenuItemIndex = ref(-1);
const searchQuery         = ref('');
const isInputFocused      = ref(false);

const searchInput      = useTemplateRef<HTMLInputElement>('searchInput');
const dropdownContainer = useTemplateRef<HTMLElement>('dropdownContainer');
const optionItems      = useTemplateRef<HTMLElement[]>('optionItems');

const filteredOptions = computed(() =>
  props.options.filter(option => {
    const searchTerm = searchQuery.value.toLowerCase();
    return (
      (option.name     && option.name.toLowerCase().includes(searchTerm)) ||
      (option.content  && option.content.toLowerCase().includes(searchTerm)) ||
      (option.summary  && option.summary.toLowerCase().includes(searchTerm)) ||
      (option.headline && option.headline.toLowerCase().includes(searchTerm))
    );
  }),
);

const stateDependantClass = computed(() => ({
  '': (props.type === 'button' || props.type === 'buttonselect'),
  'btn-dropdown btn-dropdown-large': props.type === 'buttonselectlarge',
}));

function hoverOnMenuItem(activeIndex: number): void {
  activeMenuItemIndex.value = activeIndex;
}

function toggleDropdown(): void {
  toggle();
  if (isOpen.value) {
    const firstEnabledIndex = filteredOptions.value.findIndex(option => !option.disabled);
    activeMenuItemIndex.value = firstEnabledIndex >= 0 ? firstEnabledIndex : -1;
    void nextTick(() => {
      searchInput.value?.focus();
      scrollActiveItemIntoView();
    });
  }
}

function handleFocus(): void { isInputFocused.value = true; }
function handleBlur():  void { isInputFocused.value = false; }

function findNextEnabledIndex(currentIndex: number, direction: 'up' | 'down'): number {
  const options = filteredOptions.value;
  const maxIndex = options.length - 1;
  let nextIndex = currentIndex;

  for (let i = 0; i < options.length; i++) {
    if (direction === 'down') {
      nextIndex = nextIndex >= maxIndex ? 0 : nextIndex + 1;
    } else {
      nextIndex = nextIndex <= 0 ? maxIndex : nextIndex - 1;
    }
    if (!options[nextIndex]?.disabled) return nextIndex;
  }
  return currentIndex;
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'ArrowDown') {
    activeMenuItemIndex.value = findNextEnabledIndex(activeMenuItemIndex.value, 'down');
    event.preventDefault();
    scrollActiveItemIntoView();
  } else if (event.key === 'ArrowUp') {
    activeMenuItemIndex.value = findNextEnabledIndex(activeMenuItemIndex.value, 'up');
    event.preventDefault();
    scrollActiveItemIntoView();
  } else if (event.key === 'Enter') {
    if (
      activeMenuItemIndex.value >= 0 &&
      activeMenuItemIndex.value < filteredOptions.value.length &&
      !filteredOptions.value[activeMenuItemIndex.value]?.disabled
    ) {
      setSelectedItem(activeMenuItemIndex.value);
    }
    event.preventDefault();
  }
  // Escape closes the dropdown via useDropdown's global handler — no local case needed.
}

function setSelectedItem(key: number): void {
  const option = filteredOptions.value[key];
  if (!option || option.disabled) return;
  selectedItem.value = option;
  emit('update:value', option.value);
  emit('change', option);
  searchQuery.value = '';
  close();
}

function scrollActiveItemIntoView(): void {
  void nextTick(() => {
    const container = dropdownContainer.value;
    const items     = optionItems.value;
    const item      = items?.[activeMenuItemIndex.value];
    if (container && item) {
      // Offset by ~90px to keep the sticky search area visible above the active row.
      const targetScrollTop = Math.max(0, item.offsetTop - 90);
      container.scrollTo({top: targetScrollTop, behavior: 'smooth'});
    }
  });
}

watch(searchQuery, () => {
  activeMenuItemIndex.value = filteredOptions.value.length > 0 ? 0 : -1;
});
</script>

<style lang="scss" scoped>

.dropdown {
  width: 100%;
  .dropdown-menu {
    width: 100%;

    &.dropdown-menu-large {
      width: 100%;
    }

    .dropdown-menu-box{
      padding: var(--space-0);
      min-width: 360px;
    }

    .dropdown-menu-list{
      padding: var(--space-8);
    }
    .dropdown-menu-search {
      background-color: var(--dropdown-bg);
      padding: var(--space-8) var(--space-8) var(--space-0);
      position: sticky;
      top: 0;


      .search-wrapper {
        background-color: var(--form-bg);
        border: 1px solid var(--form-border);
        border-radius: $br-sm;
        min-height: 40px;
        display: flex;

        &:hover,
        &:focus,
        &:active {
          background-color: var(--form-bg-hover);
          border: 1px solid var(--form-border-hover);
          box-shadow: var(--form-shadow-outline) !important;
        }

        &:has(input:focus) {
          background-color: var(--form-bg-focus);
          border: 1px solid var(--form-border-focus);
          box-shadow: var(--form-shadow-outline) !important;
          .tag {
            background-color: var(--tag-form-bg-hover);
            border: 1px solid var(--tag-form-border-hover);
          }
        }

        .icon {
          color: var(--text-neutral);
          padding: 0 10px;
        }
        input {
          background-color: transparent;
          border: 0;
          font-size: var(--fs-14);
          width: 100%;
          padding: 0 15px 0 0;

          &[type="search"]::-webkit-search-cancel-button {
            -webkit-appearance: none;
            height: 11px;
            width: 11px;
            border-radius: 50em;
            background-image: url("data:image/svg+xml,%3Csvg width='11' height='11' viewBox='0 0 11 11' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M9.51169 0.473251C9.77924 0.183396 10.2475 0.183396 10.515 0.473251C10.8049 0.740809 10.8049 1.20904 10.515 1.4766L6.74691 5.24471L10.515 9.03512C10.8049 9.30268 10.8049 9.77091 10.515 10.0385C10.2475 10.3283 9.77924 10.3283 9.51169 10.0385L5.74357 6.27035L1.95316 10.0385C1.6856 10.3283 1.21737 10.3283 0.949813 10.0385C0.659958 9.77091 0.659958 9.30268 0.949813 9.03512L4.71793 5.24471L0.949813 1.4766C0.659958 1.20904 0.659958 0.740809 0.949813 0.473251C1.21737 0.183396 1.6856 0.183396 1.95316 0.473251L5.74357 4.24137L9.51169 0.473251Z' fill='%23999CAF'/%3E%3C/svg%3E%0A");
            background-size: contain;
            cursor: pointer;
          }

          &::-webkit-input-placeholder { /* Edge */
            color: var(--text-neutral);
          }

          &:-ms-input-placeholder { /* Internet Explorer 10-11 */
            color: var(--text-neutral);
          }

          &::placeholder {
            color: var(--text-neutral);
          }
        }
      }
    }

    .dropdown-item {
      display: flex;
      align-items: center;
      gap: var(--space-8);
      padding: var(--space-8) var(--space-8);
      background-color: var(--transparent);
      border: 1px solid var(--transparent);
      font-size: var(--fs-14);
      border-radius: $br-sm;
      cursor: pointer;


      &:hover,
      &.active {
        background-color: var(--item-blue-bg-hover);
        border: 1px solid var(--item-blue-border-hover);
        .key{
          span{
            display: flex;
          }
        }
      }

      &.disabled {
        opacity: 0.5;
        cursor: not-allowed !important;
        pointer-events: none;

        &:hover {
          background-color: var(--transparent);
          border: 1px solid var(--transparent);
        }
      }

      .icon {
        width: 30px;
        display: flex;
        align-items: center;
        margin: var(--space-0);
        color: var(--text-neutral);

        img {
          width: 20px;
          height: 20px;
        }
      }

      .content {
        flex: 1;
        h6 {
          margin: var(--space-0);
          font-size: var(--fs-14);
          font-weight: 500;
        }
        p {
          margin: var(--space-0);
          font-size: var(--fs-12);
          color: var(--text-neutral);
        }
      }
      .key{
        display: flex;
        width: 24px;
        height: 24px;
        span{
          display: none;
          justify-content: center;
          align-items: center;
          width: 24px;
          height: 24px;
          border-radius: $br-sm;
          font-size: var(--fs-12);
          color: var(--text-dark);
          background-color: var(--button-key-bg);
          border: 1px solid var(--button-key-border);
          border-bottom: 2px solid var(--button-key-border);
        }
      }
    }

    .create-item {
      .content{
        h6{
          font-weight: 400;
        }
      }

      &-tag{
        padding: var(--space-2) var(--space-4);
        background-color: var(--tag-create-bg);
        border: 1px solid var(--tag-create-border);
        border-radius: $br-xs;
        font-weight: 500;
        margin-left: var(--space-2);
      }
    }

    .no-results {
      padding: 10px;
      text-align: center;
      color: var(--text-neutral);
    }
  }
}




.selectbox-state {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  &-content {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex: 1;
    min-width: 0;
    padding-right: var(--space-8);

    > span:not(.icon) {
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
    }
  }
}



.icon {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 5px;
}
</style>
